import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
    FaSignInAlt, FaChurch, FaUserShield, FaGlobeAmericas, FaSpinner, 
    FaSun, FaMoon, FaVolumeUp, FaVolumeMute, FaBullhorn, FaTimes, FaUsers 
} from 'react-icons/fa';
import defaultMissionImage from './assets/mission.jpeg'; 

export function LoginScreen({ onLogin, isLoading, theme, setTheme, isMuted, setIsMuted, currentTrack }) {
    const [inputName, setInputName] = useState("");
    const [inputPassword, setInputPassword] = useState("");
    const [isKoreanDetected, setIsKoreanDetected] = useState(false);

    const [siteMessage, setSiteMessage] = useState("");
    const [showMsgPopup, setShowMsgPopup] = useState(false);
    const [isNewMsg, setIsNewMsg] = useState(false);
    const [msgUpdatedAt, setMsgUpdatedAt] = useState(0);
    const [onlineCount, setOnlineCount] = useState(0);

    const fetchNoticeAndStatus = async () => {
        try {
            let tempId = localStorage.getItem('temp_visitor_id');
            if(!tempId) {
                tempId = 'guest_' + Math.random().toString(36).substring(2, 9);
                localStorage.setItem('temp_visitor_id', tempId);
            }

            const res = await axios.get(`/api/message?userId=${tempId}`);
            if (res.data) {
                setSiteMessage(res.data.message || "공지사항이 없습니다.");
                setMsgUpdatedAt(res.data.updatedAt);
                setOnlineCount(res.data.activeUsers || 1);

                const lastRead = localStorage.getItem('lastReadMsgTime');
                if (!lastRead || parseFloat(lastRead) < res.data.updatedAt) {
                    setIsNewMsg(true);
                }
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchNoticeAndStatus();
        const interval = setInterval(fetchNoticeAndStatus, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('app-theme', theme);
    }, [theme]);

    const toggleMute = (e) => {
        if (e) e.preventDefault();
        setIsMuted(!isMuted);
    };

    const toggleTheme = (e) => {
        if (e) e.preventDefault();
        setTheme(theme === 'light' ? 'dark' : 'light');
    };

    const toggleNotice = (e) => {
        if (e) e.preventDefault();
        setShowMsgPopup(!showMsgPopup);
        if (!showMsgPopup) {
            setIsNewMsg(false);
            localStorage.setItem('lastReadMsgTime', msgUpdatedAt.toString());
        }
    };

    const handleCompositionStart = () => {
        setIsKoreanDetected(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const hasActualKorean = /[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(inputPassword);

        if (hasActualKorean) {
            toast.error(
                <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                    비밀번호는 반드시 <strong>영문/숫자 모드</strong>에서 입력해야 합니다.
                </div>
            );
            return; 
        }
        if (isLoading) return; 
        onLogin(inputName, inputPassword);
    };

    const VolumeBtn = (
        <button type="button" onClick={toggleMute} className="control-icon-btn">
            {isMuted ? 
                <FaVolumeMute size={16} color={theme === 'light' ? "var(--text-secondary)" : "var(--text-secondary)"} /> : 
                <FaVolumeUp size={16} color="var(--green-color)" />
            }
        </button>
    );

    const NoticeBtn = (
        <div style={{ position: 'relative' }}>
            <button type="button" onClick={toggleNotice} className="control-icon-btn">
                <FaBullhorn size={16} color={isNewMsg ? "var(--blue-color)" : "var(--text-secondary)"} />
                {isNewMsg && <span className="custom-msg-badge">New</span>}
            </button>
        </div>
    );

    const ThemeBtn = (
        <button type="button" onClick={toggleTheme} className="control-icon-btn">
            {theme === 'light' ? 
                <FaMoon size={15} color="var(--text-secondary)" /> : 
                <FaSun size={16} color="#f1c40f" />
            }
        </button>
    );

    return (
        <div className="login-container" style={{ backgroundImage: `url(${defaultMissionImage})` }}>
            <div className="login-overlay" style={{ position: 'relative', overflow: 'hidden' }}>
                
                {showMsgPopup && (
                    <div className="login-message-popup">
                        <div className="popup-header">
                            <span><FaBullhorn /> 공지사항</span>
                            <button onClick={() => setShowMsgPopup(false)} className="control-icon-btn"><FaTimes /></button>
                        </div>
                        <div className="popup-body">
                            {siteMessage}
                        </div>
                    </div>
                )}

                <h1 className="login-title">Missionary Portal</h1>
                <p className="login-desc">로그인하여 사역 보고서를 확인하세요.</p>
                
                <form onSubmit={handleSubmit} style={{ width: '100%', marginBottom: '0px' }}>
                    <div className="input-group" style={{ marginBottom: '15px' }}>
                        <input 
                            type="text" 
                            placeholder="이름 (선교사: 개인ID / 성도: member)" 
                            value={inputName}
                            onChange={(e) => setInputName(e.target.value)}
                            required
                            autoComplete="username"
                            disabled={isLoading} 
                            style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg)', color: 'var(--text-primary)' }}
                        />
                    </div>

                    <div className="input-group" style={{ marginBottom: '20px' }}>                    
                        <input 
                            type="text" 
                            placeholder="비밀번호" 
                            value={inputPassword}
                            onCompositionStart={handleCompositionStart}
                            onCompositionUpdate={handleCompositionStart}
                            onChange={(e) => {
                                setInputPassword(e.target.value);
                                if (e.target.value === "") setIsKoreanDetected(false);
                            }}
                            required
                            autoComplete="new-password"
                            disabled={isLoading}
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck="false"
                            inputMode="text"
                            style={{ 
                                width: '100%', 
                                padding: '12px', 
                                borderRadius: '8px', 
                                border: '1px solid var(--border-color)',
                                background: 'var(--input-bg)',
                                color: 'var(--text-primary)',
                                WebkitTextSecurity: 'disc', 
                                textSecurity: 'disc'
                            }}
                        />
                    </div>

                    <button type="submit" className="login-btn" disabled={isLoading} style={{ 
                            width: '100%', 
                            padding: '10px', 
                            background: 'var(--blue-color)', 
                            color: 'var(--text-inverse)', 
                            border: 'none', 
                            borderRadius: '8px', 
                            cursor: isLoading ? 'not-allowed' : 'pointer', 
                            opacity: isLoading ? 0.7 : 1,
                            fontSize: '0.9em',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px' }} >
                        {isLoading ? <FaSpinner className="fa-spin" /> : <FaSignInAlt />} 
                        <span>{isLoading ? " 접속 정보 확인 중..." : " 로그인"}</span>
                    </button>

                    <div className="login-control-bar" style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        width: '100%', 
                        marginTop: '15px', 
                        marginBottom: '10px',
                        height: '35px',
                        justifyContent: (!isMuted && currentTrack) ? 'space-between' : 'flex-end',
                        gap: '15px'
                    }}>
                        {(!isMuted && currentTrack) ? (
                            <>
                                {VolumeBtn}
                                <div className="song-ticker-container" style={{ flex: 1, textAlign: 'center', overflow: 'hidden' }}>
                                    <span className="song-ticker-text" style={{ fontSize: '10px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                        ♪ {currentTrack.replace('.mp3', '')}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    {NoticeBtn}
                                    {ThemeBtn}
                                </div>
                            </>
                        ) : (
                            <>
                                {VolumeBtn}
                                {NoticeBtn}
                                {ThemeBtn}
                            </>
                        )}
                    </div>

                    {/* [스펙트럼 영역] 높이 15px로 축소 및 고정 */}
                    <div className="spectrum-fixed-container" style={{ height: '15px', width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '2px', paddingBottom: '0px' }}>
                        {!isMuted && currentTrack && (
                            [...Array(35)].map((_, i) => (
                                <div 
                                    key={i} 
                                    className="spectrum-bar" 
                                    style={{ 
                                        width: '4px', 
                                        backgroundColor: 'var(--accent-color)',
                                        animationDuration: `${0.4 + Math.random() * 0.6}s`,
                                        opacity: 0.8
                                    }} 
                                />
                            ))
                        )}
                    </div>
                </form>

                <div className="online-status" style={{ 
                    marginTop: '5px', 
                    fontSize: '10px', 
                    color: 'var(--green-color)', 
                    textAlign: 'center', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '5px',
                    fontWeight: 'bold'
                }}>
                    <FaUsers /> 동시 접속자: {onlineCount}명
                </div>


                <div className="login-footer" style={{ 
                    marginTop: '15px', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    fontSize: 'min(2.8vw, 11px)', 
                    color: 'var(--text-secondary)',
                    opacity: 0.8,
                    whiteSpace: 'nowrap',
                    width: '100%',
                    padding: '0 5px',
                    boxSizing: 'border-box'
                }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px', letterSpacing: '-0.3px' }}>
                        <FaGlobeAmericas style={{ fontSize: '12px' }} /> 선교사 보고
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px', letterSpacing: '-0.3px' }}>
                        <FaChurch style={{ fontSize: '12px' }} /> 성도 열람
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px', letterSpacing: '-0.3px' }}>
                        <FaUserShield style={{ fontSize: '12px' }} /> 관리자 접속
                    </span>
                </div>
            </div>
        </div>
    );
}

export function useAuth(apiUrl) {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);      
    const [isMember, setIsMember] = useState(false);    
    const [loggedInUser, setLoggedInUser] = useState(null); 
    const [authUsersList, setAuthUsersList] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);

    const sendAuthLog = useCallback(async (action, actorName, targetUserId = null) => {
        try {
            await axios.post(`${apiUrl}/logs`, {
                action,
                filename: '-',
                actor: actorName,
                targetUserId
            });
        } catch (error) { console.error(error); }
    }, [apiUrl]);

    const fetchUserList = useCallback(async () => {
        try {
            const response = await axios.get(`${apiUrl}/users`);
            setAuthUsersList(response.data);
        } catch (e) { console.error(e); }
    }, [apiUrl]);

    const refreshSingleUser = useCallback(async (userId) => {
        try {
            const response = await axios.get(`${apiUrl}/users`);
            const myData = response.data.find(u => u.id === userId);
            if (myData) setAuthUsersList([myData]);
        } catch (e) { console.error(e); }
    }, [apiUrl]);

    useEffect(() => {
        const restoreSession = async () => {
            const storedAuth = localStorage.getItem('mission_auth');
            if (storedAuth) {
                try {
                    const parsed = JSON.parse(storedAuth);
                    const { role, user } = parsed;
                    if (role === 'ADMIN') {
                        setIsAdmin(true); setIsLoggedIn(true);
                        const res = await axios.get(`${apiUrl}/users`);
                        setAuthUsersList(res.data);
                    } else if (role === 'MEMBER') {
                        setIsMember(true); setIsLoggedIn(true);
                        const res = await axios.get(`${apiUrl}/users`);
                        setAuthUsersList(res.data);
                    } else if (role === 'USER' && user) {
                        setLoggedInUser(user); setIsLoggedIn(true);
                        const res = await axios.get(`${apiUrl}/users`);
                        const myData = res.data.find(u => u.id === user.id);
                        if (myData) setAuthUsersList([myData]);
                    }
                } catch (e) { localStorage.removeItem('mission_auth'); }
            }
            setIsLoading(false);
        };
        restoreSession();
    }, [apiUrl, fetchUserList, refreshSingleUser]); 

    const login = async (name, password) => {
        if (isLoading) return;
        toast.dismiss(); 
        setIsLoading(true);
        const toastId = "auth-process-toast";
        toast.loading("인증 확인 중...", { toastId: toastId });
        
        try {
            const loginRes = await axios.post(`${apiUrl}/login`, { 
                name: name.trim(), 
                password: password 
            });
            
            if (loginRes.data.success) {
                const responseData = loginRes.data.user;
                const userRole = responseData.role; 

                if (userRole === 'ADMIN') {
                    setIsAdmin(true); setIsMember(false); setIsLoggedIn(true); setLoggedInUser(null);
                    localStorage.setItem('mission_auth', JSON.stringify({ role: 'ADMIN', user: responseData })); 
                    await fetchUserList();
                    await sendAuthLog('LOGIN', 'Administrator', responseData.id);
                    toast.update(toastId, { render: "관리자 접속", type: "success", isLoading: false, autoClose: 1000 });
                } else if (userRole === 'MEMBER') {
                    setIsMember(true); setIsAdmin(false); setIsLoggedIn(true); setLoggedInUser(null);
                    localStorage.setItem('mission_auth', JSON.stringify({ role: 'MEMBER', user: responseData }));
                    await fetchUserList();
                    await sendAuthLog('LOGIN', 'Church Member', responseData.id);
                    toast.update(toastId, { render: "성도 접속", type: "success", isLoading: false, autoClose: 1000 });
                } else {
                    setLoggedInUser(responseData); setIsAdmin(false); setIsMember(false); setIsLoggedIn(true);
                    localStorage.setItem('mission_auth', JSON.stringify({ role: 'USER', user: responseData }));
                    await refreshSingleUser(responseData.id);
                    await sendAuthLog('LOGIN', responseData.name, responseData.id);
                    toast.update(toastId, { render: `${responseData.name} 선교사님 환영합니다.`, type: "success", isLoading: false, autoClose: 1000 });
                }
            }
        } catch (err) {
            const serverMsg = err.response?.data?.error || "정보가 일치하지 않습니다.";
            toast.update(toastId, { render: serverMsg, type: "error", isLoading: false, autoClose: 1500 });
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        let actorName = "Unknown";
        let targetId = null;

        if (isAdmin) actorName = "Administrator";
        else if (isMember) actorName = "Church Member";
        else if (loggedInUser) {
            actorName = loggedInUser.name;
            targetId = loggedInUser.id;
        }

        if (isLoggedIn) {
            await sendAuthLog('LOGOUT', actorName, targetId);
        }

        localStorage.removeItem('mission_auth'); 
        setIsLoggedIn(false); 
        setLoggedInUser(null); 
        setIsAdmin(false); 
        setIsMember(false); 
        setAuthUsersList([]);
        toast.info("안전하게 로그아웃 되었습니다.");
    };

    const refreshAuthData = async () => {
        if (!isLoggedIn) return;
        if (isAdmin || isMember) await fetchUserList();
        else if (loggedInUser) await refreshSingleUser(loggedInUser.id);
    };

    return { isLoggedIn, loggedInUser, isAdmin, isMember, authUsersList, login, logout, refreshAuthData, isLoading };
}